import type { CSSProperties } from 'react';
import * as portalRules from '@buildbee/domain/portal';
import { cn } from '@buildbee/ui';
import { Logo } from './Logo';

/**
 * Company branding shared by the client portal and the company sign-in page (/c/<slug>).
 * Both read the same public, rate-limited lookup (`portal.auth.branding`), which returns only
 * branding fields: name, slug, logo URL and colours.
 */
export type CompanyBrand = {
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
  /** Sign-in company code (filled in and locked on the company's sign-in page). */
  companyCode?: string | null;
};

/** CSS variables that re-skin the design system's primary colour with the company colour. */
export function brandStyle(primaryColor: string | null | undefined): CSSProperties | undefined {
  if (!primaryColor || !/^#[0-9a-f]{6}$/i.test(primaryColor)) return undefined;
  return {
    ['--bb-primary' as string]: primaryColor,
    ['--bb-primary-foreground' as string]: portalRules.contrastText(primaryColor),
    ['--bb-ring' as string]: primaryColor,
  } as CSSProperties;
}

/**
 * Co-branding: the WRKRBZ mark always shows; when the company has uploaded a logo it sits beside
 * the mark after a thin divider. The company logo never replaces the WRKRBZ mark.
 */
export function CompanyMark({ brand, size = 'md', className }: { brand: Pick<CompanyBrand, 'name' | 'logoUrl'>; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const box = size === 'lg' ? 'h-12' : size === 'sm' ? 'h-7' : 'h-8';
  const mark = size === 'lg' ? 'size-12' : size === 'sm' ? 'size-7' : 'size-8';
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-2.5', className)} data-testid="co-brand">
      <Logo className={cn(mark, 'shrink-0')} label="WRKRBZ" />
      {brand.logoUrl ? (
        <>
          <span aria-hidden className={cn('w-px bg-border', size === 'lg' ? 'h-9' : 'h-6')} />
          <img src={brand.logoUrl} alt={brand.name} className={cn('w-auto max-w-[160px] object-contain', box)} />
        </>
      ) : null}
    </span>
  );
}

/** Up to two initials for a company with no logo ("Summit Builders" -> "SB"). */
export function companyInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
  const letters = words.slice(0, 2).map((w) => [...w.replace(/[^\p{L}\p{N}]/gu, '')][0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

/**
 * Logo block above the company sign-in card: the WRKRBZ mark (always) with the company's logo
 * beside it when uploaded, then the company name and "on BuildBee".
 */
export function CompanyAuthLogo({ brand }: { brand: Pick<CompanyBrand, 'name' | 'logoUrl'> }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid="company-brand">
      <CompanyMark brand={brand} size="lg" />
      <span className="font-display text-[19px] font-semibold tracking-[-0.01em]">{brand.name}</span>
      <span className="text-[12px] text-muted-foreground">on BuildBee</span>
    </div>
  );
}
